<template>
  <div>
    <v-card class="elevation-2 rounded-lg overflow-hidden border">
      <v-card-title class="d-flex align-center py-4 px-6 grey lighten-5 border-b">
        <div class="d-flex align-center">
          <v-icon color="primary" class="mr-3">mdi-link-variant</v-icon>
          <span class="text-h6 font-weight-bold grey--text text--darken-3">
            Imputaciones y Compensaciones
          </span>
        </div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          label="Buscar imputación..."
          single-line
          hide-details
          outlined
          dense
          class="max-width-300"
        ></v-text-field>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="all"
        :search="search"
        :loading="loading"
        :footer-props="{
          itemsPerPageText: 'Filas por página',
          itemsPerPageOptions: [10, 20, 30, 50, 100]
        }"
        :items-per-page="10"
        class="elevation-0"
      >
        <!-- ORiGEN COLUMN -->
        <template v-slot:item.origen_detalle="{ item }">
          <div class="py-3 d-flex align-start">
            <v-avatar size="36" color="teal lighten-5" class="mr-3 mt-1">
              <v-icon color="teal darken-2" size="20">
                {{ getOrigenIcon(item.origen_detalle) }}
              </v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-bold teal--text text--darken-3 text-subtitle-2">
                {{ item.origen_detalle }}
              </div>
              <div class="d-flex align-center text-caption grey--text text--darken-1 mt-1">
                <v-icon size="14" class="mr-1">mdi-calendar</v-icon>
                <span>{{ item.origen_fecha | moment('DD/MM/YYYY') }}</span>
                <span class="mx-2">•</span>
                <v-icon size="14" class="mr-1">mdi-cash</v-icon>
                <span>Original: ${{ item.origen_importe | formatToTwoDecimals }}</span>
              </div>
              <div v-if="item.origen_cantidad > 0" class="text-caption text--secondary mt-0.5">
                Kilos originales: {{ item.origen_cantidad | formatToThreeDecimals }} Kg.
              </div>
            </div>
          </div>
        </template>

        <!-- DESTINO COLUMN -->
        <template v-slot:item.destino_detalle="{ item }">
          <div class="py-3 d-flex align-start">
            <v-avatar size="36" color="indigo lighten-5" class="mr-3 mt-1">
              <v-icon color="indigo darken-2" size="20">
                {{ getDestinoIcon(item.destino_detalle) }}
              </v-icon>
            </v-avatar>
            <div>
              <div class="font-weight-bold indigo--text text--darken-3 text-subtitle-2">
                {{ item.destino_detalle }}
              </div>
              <div class="d-flex align-center text-caption grey--text text--darken-1 mt-1">
                <v-icon size="14" class="mr-1">mdi-calendar</v-icon>
                <span>{{ item.destino_fecha | moment('DD/MM/YYYY') }}</span>
                <span class="mx-2">•</span>
                <v-icon size="14" class="mr-1">mdi-cash</v-icon>
                <span>Original: ${{ item.destino_importe | formatToTwoDecimals }}</span>
              </div>
              <div v-if="item.destino_cantidad > 0" class="text-caption text--secondary mt-0.5">
                Kilos originales: {{ item.destino_cantidad | formatToThreeDecimals }} Kg.
              </div>
            </div>
          </div>
        </template>

        <!-- MONTO CANCELADO COLUMN -->
        <template v-slot:item.monto_cancelado="{ item }">
          <div class="py-2 text-right d-flex flex-column align-end">
            <v-chip
              color="success lighten-5"
              text-color="success darken-3"
              class="font-weight-bold px-3 text-subtitle-2"
              label
            >
              $ {{ item.monto_cancelado | formatToTwoDecimals }}
            </v-chip>
            
            <div v-if="item.destino_cantidad_cancelada > 0" class="text-caption grey--text text--darken-2 font-weight-medium mt-1">
              <v-icon size="14" class="mr-1 success--text">mdi-weight-kilogram</v-icon>
              <span>{{ item.destino_cantidad_cancelada | formatToThreeDecimals }} Kg. cancelados</span>
            </div>
            
            <div v-if="item.origen_cantidad_utilizada > 0 && item.origen_cantidad_utilizada !== item.destino_cantidad_cancelada" class="text-caption text--secondary mt-0.5">
              <span>(Utilizado: {{ item.origen_cantidad_utilizada | formatToThreeDecimals }} Kg.)</span>
            </div>
          </div>
        </template>

        <!-- NO DATA STATE -->
        <template v-slot:no-data>
          <div class="py-12 d-flex flex-column align-center justify-center">
            <v-avatar size="72" color="amber lighten-5" class="mb-4">
              <v-icon color="amber darken-2" size="36">mdi-link-variant-off</v-icon>
            </v-avatar>
            <div class="text-h6 font-weight-medium grey--text text--darken-3">
              No hay imputaciones registradas
            </div>
            <div class="text-body-2 grey--text text--darken-1 text-center max-width-400 mt-2">
              No se encontraron cancelaciones para la cuenta seleccionada en el período indicado.
            </div>
          </div>
        </template>

        <!-- LOADING STATE -->
        <template v-slot:loading>
          <div class="py-12 d-flex flex-column align-center justify-center">
            <v-progress-circular
              indeterminate
              color="primary"
              size="48"
              class="mb-3"
            ></v-progress-circular>
            <span class="grey--text text--darken-1 text-body-2">Cargando imputaciones de la cuenta...</span>
          </div>
        </template>
        
        <template v-slot:pageText="props">
          Filas {{ props.pageStart }} - {{ props.pageStop }} de {{ props.itemsLength }}
        </template>
      </v-data-table>
    </v-card>
  </div>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'

export default {
  name: 'ImputacionesDatatableComponent',
  data () {
    return {
      search: '',
      headers: [
        {
          text: 'Transacción Origen (Pago / Crédito)',
          value: 'origen_detalle',
          align: 'start',
          sortable: true
        },
        {
          text: 'Transacción Destino (Consumo / Débito)',
          value: 'destino_detalle',
          align: 'start',
          sortable: true
        },
        {
          text: 'Monto Cancelado',
          value: 'monto_cancelado',
          align: 'end',
          width: '240px',
          sortable: true
        }
      ]
    }
  },
  computed: {
    ...mapGetters('ImputacionesExport', ['all', 'loading', 'filters']),
    ...mapGetters('Cuentas', {
      cuentaSelected: 'selected'
    }),
    ...mapGetters('Transacciones', {
      transFilters: 'filters'
    }),
    cuentaId () {
      return this.cuentaSelected ? this.cuentaSelected.id : null
    },
    fechaFrom () {
      return this.transFilters ? this.transFilters.fecha_from : null
    },
    fechaTo () {
      return this.transFilters ? this.transFilters.fecha_to : null
    }
  },
  watch: {
    cuentaId: {
      handler: 'fetchData',
      immediate: true
    },
    fechaFrom: 'fetchData',
    fechaTo: 'fetchData'
  },
  methods: {
    ...mapActions('ImputacionesExport', ['onFetch']),
    ...mapMutations('ImputacionesExport', ['setFilters']),
    async fetchData () {
      if (!this.cuentaId) {
        this.$store.commit('ImputacionesExport/setFilters', {
          cuenta_id: null,
          fecha_from: null,
          fecha_to: null
        })
        this.$store.commit('ImputacionesExport/setAll', [])
        return
      }

      // Default a 1 año si no hay filtros específicos
      let fechaTo = this.fechaTo
      let fechaFrom = this.fechaFrom
      if (!fechaFrom || !fechaTo) {
        fechaTo = this.$moment().format('YYYY-MM-DD')
        fechaFrom = this.$moment(fechaTo).subtract(1, 'years').format('YYYY-MM-DD')
      }

      await this.setFilters({
        cuenta_id: this.cuentaId,
        fecha_from: fechaFrom,
        fecha_to: fechaTo
      })

      try {
        await this.onFetch()
      } catch (error) {
        console.error('Error al cargar imputaciones:', error)
      }
    },
    getOrigenIcon (detalle) {
      if (!detalle) return 'mdi-cash'
      const lower = detalle.toLowerCase()
      if (lower.includes('bono') || lower.includes('subsidio')) {
        return 'mdi-ticket-percent'
      }
      if (lower.includes('efectivo')) {
        return 'mdi-cash-multiple'
      }
      if (lower.includes('trf') || lower.includes('transferencia')) {
        return 'mdi-bank-transfer'
      }
      if (lower.includes('cheque')) {
        return 'mdi-card-bulleted-outline'
      }
      return 'mdi-arrow-right-bold-circle-outline'
    },
    getDestinoIcon (detalle) {
      if (!detalle) return 'mdi-file-document-outline'
      const lower = detalle.toLowerCase()
      if (lower.includes('glp') || lower.includes('kilo') || lower.includes('gas')) {
        return 'mdi-gas-cylinder'
      }
      if (lower.includes('bono') || lower.includes('subsidio')) {
        return 'mdi-ticket-percent'
      }
      return 'mdi-arrow-left-bold-circle-outline'
    }
  }
}
</script>

<style scoped>
.max-width-300 {
  max-width: 300px;
}
.max-width-400 {
  max-width: 400px;
}
.border-b {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12) !important;
}
</style>
